import React, { useCallback, useMemo } from "react";

import { Monitor } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";

import CreateComponentButton from "./CreateComponentButton";
import DataTable from "../../../../components/dataTable/DataTable";
import DataTableEmptyState from "../../../../components/dataTable/DataTableEmptyState";
import {
  applyComponentOrder,
  getComponentHref,
  pageComponentColumns,
  toPageComponentRow,
} from "../../tableParams/pageComponentColumns";

import type { JSX } from "react";

import type { PlatformComponent } from "../../../../api/ProjectAPI.types";
import type { PlatformRoutesParams } from "../../../../components/routes/PlatformRoutes";
import type { AppState } from "../../../../reducers";
import type { PageComponentRow } from "../../tableParams/pageComponentColumns.types";

interface ComponentListProps {
  onDelete: (componentId: string) => void;
  currentComponents: PlatformComponent[];
  onUpdate: (components: PlatformComponent[]) => void;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => unknown;
}

export default function ComponentList({
  onDelete,
  onUpdate,
  currentComponents,
  isLoading,
  isError,
  onRetry,
}: ComponentListProps): JSX.Element {
  const { t } = useTranslation("editor-platform");
  const history = useHistory();
  const {
    projectSlug,
    pageId,
    platform = "web",
  } = useParams<PlatformRoutesParams>();
  const smartlists = useSelector(
    (state: AppState) => state.fetchReducer.smartlists,
  );

  const rowList = useMemo(
    () =>
      currentComponents.map((component) =>
        toPageComponentRow(component, smartlists),
      ),
    [currentComponents, smartlists],
  );

  const getRowHref = useCallback(
    (row: PageComponentRow): string =>
      getComponentHref(projectSlug, pageId, platform, row.type, row.uuid),
    [projectSlug, pageId, platform],
  );

  const columnList = useMemo(
    () =>
      pageComponentColumns({
        t,
        projectSlug,
        pageId,
        platform,
        onEdit: (row) => history.push(getRowHref(row)),
        onDelete,
      }),
    [t, projectSlug, pageId, platform, history, getRowHref, onDelete],
  );

  const handleReorder = (reorderedList: PageComponentRow[]): void => {
    onUpdate(applyComponentOrder(reorderedList.map((row) => row.component)));
  };

  const emptyState = (
    <DataTableEmptyState
      action={<CreateComponentButton />}
      icon={Monitor}
      message={t("createComponentText")}
    />
  );

  return (
    <DataTable
      columns={columnList}
      data={rowList}
      emptyState={emptyState}
      getRowHref={getRowHref}
      isError={isError}
      isLoading={isLoading}
      onReorder={handleReorder}
      onRetry={onRetry}
      urlSync={false}
    />
  );
}
